import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
    LayoutDashboard, ShoppingCart, Package, Boxes, Truck,
    Receipt, BarChart3, Settings, LogOut, ChevronLeft, ChevronRight,
    ScanLine, HelpCircle, Clock, Shield, Users, Activity, X, ChefHat,
    FileText, Store, ScanBarcode, AlertTriangle, ShoppingBag, Tag, ShieldCheck,
    Wand2, Upload, Bike, Calculator, Monitor
} from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const STORE_NAV_GROUPS = [
    {
        label: 'Main',
        items: [
            { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'cashier', 'user'] },
            { path: '/pos', label: 'Point of Sale', icon: ShoppingCart, roles: ['admin', 'manager', 'cashier', 'user'] },
            { path: '/kds', label: 'Kitchen Display', icon: ChefHat, roles: ['admin', 'manager', 'cashier', 'user'] },
            { path: '/scan', label: 'Scan Product', icon: ScanLine, roles: ['admin', 'manager', 'user'] },
        ]
    },
    {
        label: 'Management',
        items: [
            { path: '/products', label: 'Products', icon: Package, roles: ['admin', 'manager', 'user'] },
            { path: '/inventory', label: 'Inventory', icon: Boxes, roles: ['admin', 'manager', 'user'] },
            { path: '/inventory/alerts', label: 'Stock Alerts', icon: AlertTriangle, roles: ['admin', 'manager', 'user'] },
            { path: '/inventory/reorder', label: 'Reorder Suggestions', icon: ShoppingBag, roles: ['admin', 'manager', 'user'] },
            { path: '/inventory-import', label: 'Import Products', icon: Upload, roles: ['admin', 'manager', 'user'] },
            { path: '/suppliers', label: 'Suppliers', icon: Truck, roles: ['admin', 'manager', 'user'] },
            { path: '/invoices/upload', label: 'AI Invoice Scan', icon: FileText, roles: ['admin', 'manager', 'user'] },
            { path: '/labels', label: 'Label Generator', icon: Tag, roles: ['admin', 'manager', 'user'] },
            { path: '/delivery', label: 'Delivery Hub', icon: Bike, roles: ['admin', 'manager', 'user'] },
            { path: '/hardware-setup', label: 'Hardware Setup', icon: Monitor, roles: ['admin', 'manager', 'user'] },
            { path: '/employees', label: 'Employees', icon: Users, roles: ['admin', 'manager'] },
        ]
    },
    {
        label: 'Analytics',
        items: [
            { path: '/closeout', label: 'Daily Closeout', icon: Calculator, roles: ['admin', 'manager', 'cashier', 'user'] },
            { path: '/closeout/history', label: 'History', icon: FileText, roles: ['admin', 'manager', 'user'] },
            { path: '/sales', label: 'Sales', icon: Receipt, roles: ['admin', 'manager', 'cashier', 'user'] },
            { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'manager', 'user'] },
            { path: '/timeclock', label: 'Time Clock', icon: Clock, roles: ['admin', 'manager', 'user'] },
        ]
    },
    {
        label: 'Setup & Support',
        items: [
            { path: '/setup', label: 'Setup Wizard', icon: Wand2, roles: ['admin', 'manager', 'user'] },
            { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin', 'user'] },
            { path: '/store-settings', label: 'Store & Features', icon: Store, roles: ['admin', 'manager', 'user'] },
            { path: '/compliance', label: 'Age Compliance', icon: ShieldCheck, roles: ['admin', 'manager', 'user'] },
            { path: '/help', label: 'Help & Training', icon: HelpCircle, roles: ['admin', 'manager', 'cashier', 'user'] },
        ]
    }
];

const SUPER_ADMIN_NAV = [
    { path: '/admin', label: 'Operator Dashboard', icon: Shield },
    { path: '/dashboard', label: 'Platform Overview', icon: Activity },
    { path: '/settings', label: 'Settings', icon: Settings },
];

function NavItem({ item, isActive, collapsed, highlight = false }) {
    const Icon = item.icon;

    const cls = cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative',
        isActive
            ? highlight
                ? 'bg-violet-600 text-white shadow-sm shadow-violet-500/30'
                : 'bg-cyan-500 text-white shadow-sm shadow-cyan-500/30'
            : highlight
                ? 'text-violet-600 dark:text-violet-300 hover:bg-violet-500/10 dark:hover:bg-violet-500/20 hover:text-violet-700 dark:hover:text-white'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
    );

    const content = (
        <Link to={item.path} data-testid={`nav-${item.path.slice(1)}`} className={cls}>
            {isActive && (
                <span className={cn(
                    'absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full',
                    highlight ? 'bg-violet-300' : 'bg-white'
                )} />
            )}
            <Icon className="shrink-0" style={{ width: '1.125rem', height: '1.125rem' }} />
            {!collapsed && <span>{item.label}</span>}
        </Link>
    );

    if (collapsed) {
        return (
            <Tooltip key={item.path}>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 text-xs">
                    {item.label}
                </TooltipContent>
            </Tooltip>
        );
    }

    return content;
}

export function Sidebar({ collapsed, onToggle, onMobileClose, isMobile }) {
    const { user, isSuperAdmin } = useAuth();
    const location = useLocation();

    const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
    };

    if (isMobile) {
        return (
            <div className="fixed inset-0 z-50 flex">
                <div
                    className={cn(
                        'w-64 flex flex-col h-full',
                        isSuperAdmin
                            ? 'bg-violet-50 dark:bg-[hsl(265,40%,5%)] border-r border-violet-200 dark:border-violet-500/10'
                            : 'bg-slate-50 dark:bg-[hsl(222,47%,5%)] border-r border-slate-200 dark:border-white/5'
                    )}
                >
                    {/* Mobile Sidebar Content */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/5">
                        <span className="font-bold text-slate-900 dark:text-white">Menu</span>
                        <button onClick={onMobileClose} className="p-2 text-slate-600 dark:text-slate-400">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <nav className="flex-1 overflow-y-auto p-2">
                        {isSuperAdmin ? (
                            SUPER_ADMIN_NAV.map(item => (
                                <NavItem
                                    key={item.path}
                                    item={item}
                                    isActive={location.pathname === item.path}
                                    collapsed={false}
                                    highlight
                                />
                            ))
                        ) : (
                            STORE_NAV_GROUPS.map(group => {
                                const visible = group.items.filter(item => item.roles.includes(user?.role));
                                if (visible.length === 0) return null;
                                return (
                                    <div key={group.label}>
                                        <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
                                            {group.label}
                                        </p>
                                        {visible.map(item => (
                                            <NavItem
                                                key={item.path}
                                                item={item}
                                                isActive={location.pathname === item.path}
                                                collapsed={false}
                                            />
                                        ))}
                                    </div>
                                );
                            })
                        )}
                    </nav>
                </div>
                <div className="flex-1 bg-black/50" onClick={onMobileClose} />
            </div>
        );
    }

    // Desktop layout
    return (
        <TooltipProvider delayDuration={0}>
            <div className={cn(
                'h-screen flex flex-col fixed left-0 top-0 z-40 transition-all duration-300',
                isSuperAdmin
                    ? 'bg-violet-50 dark:bg-[hsl(265,40%,5%)] border-r border-violet-200 dark:border-violet-500/10'
                    : 'bg-slate-50 dark:bg-[hsl(222,47%,5%)] border-r border-slate-200 dark:border-white/5',
                collapsed ? 'w-16' : 'w-64'
            )}>
                {/* Brand Header */}
                <div className={cn(
                    'h-16 flex items-center border-b shrink-0',
                    isSuperAdmin ? 'border-violet-200 dark:border-violet-500/10' : 'border-slate-200 dark:border-white/5',
                    collapsed ? 'justify-center px-3' : 'px-4 gap-3'
                )}>
                    {isMobile && onMobileClose && (
                        <button
                            onClick={onMobileClose}
                            className="text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-white/10 border-0 cursor-pointer p-2 rounded-md mr-1 text-lg leading-none"
                        >
                            ✕
                        </button>
                    )}
                    <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 shadow-lg',
                        isSuperAdmin
                            ? 'bg-gradient-to-br from-violet-500 to-indigo-600 shadow-violet-500/20'
                            : 'bg-transparent'
                    )}>
                        {isSuperAdmin
                            ? <Shield className="h-4 w-4 text-white" />
                            : <img src="/mezgebpos-logo.png" alt="Mezgeb POS" className="h-20 w-auto object-contain" />
                        }
                    </div>
                    {!collapsed && (
                        <div className="min-w-0">
                            <p className="font-black text-slate-900 dark:text-white text-sm leading-none tracking-tight">Mezgeb POS</p>
                            <p className={cn(
                                'text-[10px] mt-0.5 truncate font-semibold',
                                isSuperAdmin ? 'text-violet-600 dark:text-violet-400' : 'text-slate-500 dark:text-slate-500'
                            )}>
                                {isSuperAdmin ? 'Operator Console' : 'mezgebpos.com'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin py-3 px-2 space-y-0.5">
                    {isSuperAdmin ? (
                        <div>
                            {!collapsed && (
                                <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-violet-500/70 dark:text-violet-500/70 mb-1">
                                    Operator
                                </p>
                            )}
                            {SUPER_ADMIN_NAV.map(item => (
                                <NavItem
                                    key={item.path}
                                    item={item}
                                    isActive={location.pathname === item.path}
                                    collapsed={collapsed}
                                    highlight
                                />
                            ))}
                        </div>
                    ) : (
                        STORE_NAV_GROUPS.map(group => {
                            const visible = group.items.filter(item => item.roles.includes(user?.role));
                            if (visible.length === 0) return null;
                            return (
                                <div key={group.label}>
                                    {!collapsed && (
                                        <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">
                                            {group.label}
                                        </p>
                                    )}
                                    {collapsed && <div className="h-3" />}
                                    {visible.map(item => (
                                        <NavItem
                                            key={item.path}
                                            item={item}
                                            isActive={location.pathname === item.path}
                                            collapsed={collapsed}
                                        />
                                    ))}
                                </div>
                            );
                        })
                    )}
                </nav>

                {/* User Footer */}
                <div className={cn(
                    'shrink-0 border-t p-3 space-y-1',
                    isSuperAdmin ? 'border-violet-200 dark:border-violet-500/10' : 'border-slate-200 dark:border-white/5'
                )}>
                    {!collapsed ? (
                        <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 cursor-default">
                            <div className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center shrink-0',
                                isSuperAdmin
                                    ? 'bg-gradient-to-br from-violet-500 to-indigo-600'
                                    : 'bg-gradient-to-br from-violet-500 to-indigo-600'
                            )}>
                                <span className="text-white text-xs font-bold">{initials}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{user?.name}</p>
                                <p className={cn(
                                    'text-[11px] capitalize',
                                    isSuperAdmin ? 'text-violet-600 dark:text-violet-400 font-semibold' : 'text-slate-500 dark:text-slate-500'
                                )}>
                                    {isSuperAdmin ? 'Super Admin' : user?.role}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="flex justify-center py-1 cursor-default">
                                    <div className={cn(
                                        'w-8 h-8 rounded-full flex items-center justify-center',
                                        isSuperAdmin
                                            ? 'bg-gradient-to-br from-violet-500 to-indigo-600'
                                            : 'bg-gradient-to-br from-violet-500 to-indigo-600'
                                    )}>
                                        <span className="text-white text-xs font-bold">{initials}</span>
                                    </div>
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 text-xs">
                                <p className="font-semibold">{user?.name}</p>
                                <p className="text-[10px] opacity-80 capitalize">{isSuperAdmin ? 'Super Admin' : user?.role}</p>
                            </TooltipContent>
                        </Tooltip>
                    )}

                    {/* Logout */}
                    {collapsed ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex justify-center py-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                >
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 text-xs">
                                Log out
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                            <LogOut className="w-[1.125rem] h-[1.125rem]" />
                            <span>Log out</span>
                        </button>
                    )}
                </div>

                {/* Collapse Toggle */}
                {!isMobile && (
                    <button
                        onClick={onToggle}
                        className={cn(
                            'absolute -right-3 top-20 w-6 h-6 rounded-full flex items-center justify-center shadow-md border',
                            'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400'
                        )}
                    >
                        {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
                    </button>
                )}
            </div>
        </TooltipProvider>
    );
}
